export const GUIDE_TOUR_TARGET_TEST_ID_ATTRIBUTE = 'data-testid' as const

/** @deprecated Legacy markers in app code. Prefer `data-testid`. */
export const GUIDE_TOUR_TARGET_ATTRIBUTE = 'data-guide-tour-id' as const

export const GUIDE_TOUR_TARGET_MISSING_DELAY_MS = 300

export type SpotlightBounds = {
  top: number
  left: number
  width: number
  height: number
}

const getGuideTourTargetSelectors = (targetId: string): string[] => {
  const escapedTargetId = CSS.escape(targetId)

  return [
    `[${GUIDE_TOUR_TARGET_TEST_ID_ATTRIBUTE}="${targetId}"]`,
    `#${escapedTargetId}`,
    `[data-guide-tour-id="${targetId}"]`,
  ]
}

export const getGuideTourTargetSelector = (targetId: string): string =>
  getGuideTourTargetSelectors(targetId).join(', ')

const findGuideTourTargetInScope = (
  scope: ParentNode,
  targetId: string
): HTMLElement | null => {
  for (const selector of getGuideTourTargetSelectors(targetId)) {
    const match = scope.querySelector(selector)

    if (match instanceof HTMLElement) {
      return match
    }
  }

  const descendants = scope.querySelectorAll('*')

  for (const descendant of descendants) {
    if (!(descendant instanceof HTMLElement) || !descendant.shadowRoot) {
      continue
    }

    const inShadow = findGuideTourTargetInScope(descendant.shadowRoot, targetId)

    if (inShadow) {
      return inShadow
    }
  }

  return null
}

export const findGuideTourTargetElement = (
  targetId: string,
  root: ParentNode = document
): HTMLElement | null => findGuideTourTargetInScope(root, targetId)

export const getGuideTourTargetBounds = (
  element: HTMLElement,
  padding = 8
): SpotlightBounds => {
  const rect = resolveGuideTourTargetRect(element)

  return {
    top: rect.top - padding,
    left: rect.left - padding,
    width: rect.width + padding * 2,
    height: rect.height + padding * 2,
  }
}

const hasMeasurableBounds = (
  bounds: Pick<SpotlightBounds, 'width' | 'height'>
): boolean => bounds.width > 0 && bounds.height > 0

const HEXA_V2_NAV_BAR_TAG = 'hexa-v2-nav-bar'

const findHexaV2NavBarHost = (element: HTMLElement): HTMLElement | null => {
  if (element.tagName.toLowerCase() === HEXA_V2_NAV_BAR_TAG) {
    return element
  }

  const child = element.querySelector(HEXA_V2_NAV_BAR_TAG)

  return child instanceof HTMLElement ? child : null
}

const findHexaV2NavBarSurface = (navBar: HTMLElement): HTMLElement | null => {
  const shadowRoot = navBar.shadowRoot

  if (!shadowRoot) {
    return null
  }

  const partNavBar = shadowRoot.querySelector('[part="nav-bar"]')

  if (partNavBar instanceof HTMLElement) {
    return partNavBar
  }

  return null
}

const tryResolveHexaV2NavBarBounds = (element: HTMLElement): DOMRect | null => {
  const navBar = findHexaV2NavBarHost(element)

  if (!navBar) {
    return null
  }

  const navBarRect = navBar.getBoundingClientRect()

  if (hasMeasurableBounds(navBarRect)) {
    return navBarRect
  }

  const surface = findHexaV2NavBarSurface(navBar)

  if (!surface) {
    return null
  }

  const surfaceRect = surface.getBoundingClientRect()

  return hasMeasurableBounds(surfaceRect) ? surfaceRect : null
}

const preferHexaV2NavBarBoundsForTourAnchor = (
  element: HTMLElement
): DOMRect | null => {
  if (!element.hasAttribute(GUIDE_TOUR_TARGET_TEST_ID_ATTRIBUTE)) {
    return null
  }

  const navBarRect = tryResolveHexaV2NavBarBounds(element)

  if (!navBarRect) {
    return null
  }

  const hostRect = element.getBoundingClientRect()

  if (!hasMeasurableBounds(hostRect)) {
    return navBarRect
  }

  const hostArea = hostRect.width * hostRect.height
  const navBarArea = navBarRect.width * navBarRect.height

  return navBarArea > hostArea ? navBarRect : null
}

const GUIDE_TOUR_SHADOW_INTERACTIVE_SELECTORS = [
  'input[type="checkbox"]',
  'input',
  'label',
  'button',
  '[role="checkbox"]',
  '[role="combobox"]',
  '[role="button"]',
  '[role="tab"]',
  'a',
].join(', ')

const findMeasurableRectInShadow = (shadowRoot: ShadowRoot): DOMRect | null => {
  const shadowCandidates = shadowRoot.querySelectorAll(
    GUIDE_TOUR_SHADOW_INTERACTIVE_SELECTORS
  )

  for (const candidate of shadowCandidates) {
    if (!(candidate instanceof HTMLElement)) {
      continue
    }

    const shadowRect = candidate.getBoundingClientRect()

    if (hasMeasurableBounds(shadowRect)) {
      return shadowRect
    }
  }

  return null
}

export const resolveGuideTourTargetRect = (element: HTMLElement): DOMRect => {
  const preferredNavBarRect = preferHexaV2NavBarBoundsForTourAnchor(element)

  if (preferredNavBarRect) {
    return preferredNavBarRect
  }

  const hostRect = element.getBoundingClientRect()

  if (hasMeasurableBounds(hostRect)) {
    return hostRect
  }

  const hexaNavBarRect = tryResolveHexaV2NavBarBounds(element)

  if (hexaNavBarRect) {
    return hexaNavBarRect
  }

  const shadowRoot = element.shadowRoot

  if (shadowRoot) {
    const shadowRect = findMeasurableRectInShadow(shadowRoot)

    if (shadowRect) {
      return shadowRect
    }
  }

  const nestedInteractive = element.querySelector(
    'hexa-v2-button, hexa-v2-checkbox, hexa-v2-dropdown-form, button, input, label'
  )

  if (nestedInteractive instanceof HTMLElement) {
    const nestedRect = resolveGuideTourTargetRect(nestedInteractive)

    if (hasMeasurableBounds(nestedRect)) {
      return nestedRect
    }
  }

  const nestedButton = element.querySelector('button')

  if (nestedButton instanceof HTMLElement) {
    const nestedRect = nestedButton.getBoundingClientRect()

    if (hasMeasurableBounds(nestedRect)) {
      return nestedRect
    }
  }

  return hostRect
}
